/**
 * @file UserLandingView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import { Button, Dialog, DialogContent, Grid, Paper, Typography } from "@mui/material";
import "./AdminHome.css"
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { ThemeProvider } from "@emotion/react";
import { noTextTransform, roboto700Text16Bold } from "../../../utils/theme/CommonTheme";
import { AppProgressBar } from "../../../utils/AppProgressBar";
import { Clinic } from "../../../../services/models/Clinic";
import Snackbar from "@mui/material/Snackbar";
import { CSSTransition } from "react-transition-group";
import { AppAlert } from "../../../utils/AppAlert";
import { BaseComponent } from "../../base/BaseComponent";
import { AdminViewModel } from "../viewmodels/AdminViewModel";
import { ApiError } from "../../../../services/models/ApiError";
import { AdminHeaderView } from "./AdminHeaderView";
import CustomAdminRows from "./CustomAdminRows";
import { NewAdminViewController } from "../controllers/NewAdminViewController";
import { DeleteUserView } from "./DeleteUserView";
import { AdminRow } from "../../../../services/models/AdminRow";
import { GetAdminResponse } from "../../../../services/models/GetAdminResponse";
import { NoDataFound } from "../../../common/customviews/NoDataFound";
import { Admin } from "../../../../services/models/Admin";
import { BaseProps } from "../../base/BaseProps";
import { AdminCreatedSuccess, AdminDeleteSuccess, NoAdminFound, ResendAdminSuccess } from "../../../utils/AppConstant";

/**
 * A type of Component state
 */
type CompState = {
    isNewAdminDialogOpen: boolean, mainLoadding: boolean, isDeleteAdminDialogOpen: boolean, isDataLoaded: boolean,
    showSnackBar: boolean, snackBarErrorMessage: string, clinic?: Clinic,
    reloadPage: boolean, deleteAdminRow?: AdminRow, successAlert: boolean
}

/**
 * A class definition for component UserLandingView
 */
export class UserLandingView extends BaseComponent<BaseProps, CompState> {

    viewModel: AdminViewModel;
    constructor(props: BaseProps) {
        super(props)
        this.viewModel = new AdminViewModel();
        const selectedClinic = this.getSelectedClinic();
        let clinic: Clinic = new Clinic();

        if(!this.isEmpty(selectedClinic)){
            clinic = JSON.parse(selectedClinic) as Clinic; // eslint-disable-line
            this.logger.verbose(`UserLandingView(Clinic- ${clinic})`, UserLandingView.name);
        }

        this.state = {
            isNewAdminDialogOpen: false, mainLoadding: false, isDeleteAdminDialogOpen: false,
            isDataLoaded: false, showSnackBar: false, reloadPage: false,
            snackBarErrorMessage: "Unknown Error", clinic: clinic, successAlert: false
        };

        this.getAdminResponse = new GetAdminResponse();
        this.getAdminResponse.rows = [];
    }

    private getAdminResponse: GetAdminResponse;

    async componentDidMount(): Promise<void> {
        this.logger.verbose(`UserLandingView(mounted)`, UserLandingView.name);
        this.subscribeApiEvenets();
        this.loadAdminUsers(this.state.clinic?.id!!); // eslint-disable-line
    }

    componentWillUnmount(): void {
        try{
            this.unSubscribeApiEvenets();
        } catch(e: unknown) {
            this.logger.error(JSON.stringify(e), UserLandingView.name);
        }
    }

    /**
     * Subscribes to Api Events
     */
    private subscribeApiEvenets = ()=> {
        try {
            this.viewModel.onLoadedAdminList.subscribe(data => {
                this.getAdminResponse = data;
                this.logger.verbose(`loadAdminUsers()`, UserLandingView.name);
                this.dataLoaded();
            });
            this.viewModel.onError.subscribe((error: ApiError) => {
                this.dataLoaded();
                this.showSnackBarError(error?.message!!, false); // eslint-disable-line
            });

            this.viewModel.onResentInviteAdmin.subscribe(data => {
                this.logger.verbose(`onResentInviteAdmin(${JSON.stringify(data)})`, UserLandingView.name);
                this.logger.verbose(`onResentInviteAdmin(${data.message})`, UserLandingView.name);
                this.hideMainLoading();
                this.showSnackBarError(ResendAdminSuccess, true); // eslint-disable-line
            });

            this.viewModel.onResendInviteAdminError.subscribe((error: ApiError) => {
                this.hideMainLoading();
                this.showSnackBarError(error?.message!!, false); // eslint-disable-line
            });

            this.viewModel.onAdminDeleted.subscribe(data => {
                this.logger.verbose(`onAdminDeleted(${JSON.stringify(data)})`, UserLandingView.name);
                this.logger.verbose(`onAdminDeleted(${data.message})`, UserLandingView.name);
                this.hideMainLoading();
                this.showSnackBarError(AdminDeleteSuccess, true); // eslint-disable-line
                this.loadAdminUsers(this.state.clinic?.id!!); // eslint-disable-line
            });

            this.viewModel.onAdminDeletedError.subscribe((error: ApiError) => {
                this.hideMainLoading();
                this.showSnackBarError(error?.message!!, false); // eslint-disable-line
            });

        } catch (e: unknown){
            this.logger.verbose(`subscribeApiEvenets()`, UserLandingView.name);
            this.logger.error(JSON.stringify(e), UserLandingView.name);

        }
    }

    /**
     * Unsubscribes to Api Events
     */
    private unSubscribeApiEvenets = () =>{
        this.viewModel.onLoadedAdminList.unsubscribe();
        this.viewModel.onError.unsubscribe();

        this.viewModel.onResentInviteAdmin.unsubscribe();
        this.viewModel.onResendInviteAdminError.unsubscribe();
    }

    /**
     * Loads list of admins
     * @param clinicId string
     * @returns 
     */
    private loadAdminUsers = (clinicId: string)=> {
        this.logger.warning(`clinicId(${clinicId})`, UserLandingView.name);
        this.dataReLoad();
        const accessToken = this.validateAndGetSession(UserLandingView.name);
        this.viewModel.getAdminList(clinicId, accessToken);
    }

    /**
     * Sets data reload state
     */
    private dataReLoad = () => {
        this.setState({ isDataLoaded: false });
    }

    /**
     * Sets states once data is loaded
     */
    private dataLoaded = () => {
        this.setState({ isDataLoaded: true });
    }

    /**
     * Shows new Admin dialog
     */
    private showNewAdminDialog = () => {
        this.setState({ isNewAdminDialogOpen: true });
    }

    /**
     * Shows delete admin dialog
     * @param row AdminRow
     */
    private showDeleteAdminDialog = (row: AdminRow) => {
        this.setState({ isDeleteAdminDialogOpen: true, deleteAdminRow: row });
    };

    /**
     * Dismises delete patient dialog
     */
    private dismissDeleteAdminDialog = () => {
        this.setState({ isDeleteAdminDialogOpen: false });
    }

    /**
     * Dismisses new admin dialog
     */
    private dismissNewAdminDialog = () => {
        this.setState({ isNewAdminDialogOpen: false });
    }

    /**
     * Shows main loading
     */
    private showMainLoading = () => {
        this.setState({ mainLoadding: true });
    }

    /**
     * Hides main loading
     */
    private hideMainLoading = () => {
        this.setState({ mainLoadding: false });
    }

    /**
     * Hides snack bar
     */
    private hideSnackBarError = () => {
        this.setState({ showSnackBar: false });
    }

    /**
     * Shows snackbar
     * @param message string 
     */
    private showSnackBarError = (message: string, isSuccessAlert: boolean) => {
        this.setState({ showSnackBar: true, snackBarErrorMessage: message, successAlert: isSuccessAlert})
    }

    /**
     * Calls on selection of the clinic
     * @param clinic Clinic
     */
    public onClinicSelected = (clinic: Clinic) => {
        this.setState({
            clinic: clinic
        });
        this.loadAdminUsers(clinic.id!!); // eslint-disable-line
    }

    /**
     * Deletes admin
     * @param row AdminRow
     */
    private performAdminDelete = async (row: AdminRow) => {
        const email = row.bscRep ? row.bscRep.email : row.hcp?.email;
        this.logger.verbose(`performAdminDelete() - ${email}`, UserLandingView.name);
        const accessToken = this.validateAndGetSession(UserLandingView.name);
        this.showMainLoading();
        this.viewModel.deleteAdmin(email!!, this.state.clinic?.id!!, accessToken); // eslint-disable-line
    }

    /**
     * Triggers when new admin is created
     * @param id string
     */
    private onNewAdminCreated = () => {
        this.logger.verbose(`onNewAdminCreated()`, UserLandingView.name);
        this.setState({ reloadPage: true });
        this.showSnackBarError(AdminCreatedSuccess, true);
        this.loadAdminUsers(this.state.clinic?.id!!); //eslint-disable-line
    }

    /**
     * Deletes user
     */
    private onUserDeleted = () => {
        this.logger.verbose(`onUserDeleted`, UserLandingView.name);
        this.setState({ isDeleteAdminDialogOpen: false });
        this.performAdminDelete(this.state.deleteAdminRow!!); // eslint-disable-line
    }

    private onResendInvite = (admin: Admin)=> {
        const accessToken = this.validateAndGetSession(UserLandingView.name);
        this.showMainLoading();
        this.viewModel.resentAdminInvite(this.state.clinic?.id !!, admin.email, accessToken); // eslint-disable-line
    }

    render() {

        if (this.state.isDataLoaded) {
            this.logger.verbose("Admin present in the list", UserLandingView.name);
            return <React.Fragment>
                <Grid className="AdminTableBox">
                    <Grid container spacing={0} >
                        <Grid item xs={5} container>
                            <ThemeProvider theme={roboto700Text16Bold}>
                                <Typography className="UserHeader"> Users ({this.getAdminResponse?.rows?.length})</Typography>
                            </ThemeProvider>
                        </Grid>
                        <Grid item xs={7} container justifyContent="flex-end">
                            <ThemeProvider theme={noTextTransform}>
                                <Button onClick={this.showNewAdminDialog} size="small" variant="contained"
                                    disableElevation disabled={false} className="AdminAddNewRecordButton">
                                    + &nbsp;&nbsp;  Add a New User
                                </Button>
                            </ThemeProvider>
                        </Grid>
                    </Grid>

                    {this.getAdminResponse?.rows?.length == 0 && <NoDataFound message= {NoAdminFound} />}
                    {// eslint-disable-next-line
                        (this.getAdminResponse?.rows!!.length  > 0) && <TableContainer className="AdminTableContainer" component={Paper} sx={{ width: '100%' }}>
                            <Table sx={{ minWidth: 500, display: "block" }} aria-label="custom pagination table">
                                <AdminHeaderView />
                                <CustomAdminRows deleteAdmin={this.showDeleteAdminDialog} getAdminResponse={this.getAdminResponse!} onResendInvite={this.onResendInvite} />
                            </Table>
                        </TableContainer>}
                    <Dialog onClose={this.dismissNewAdminDialog} className="AddNewUserDialogContainer" aria-labelledby="customized-dialog-title" open={this.state.isNewAdminDialogOpen} fullWidth={true}
                        maxWidth="lg" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem", maxWidth:"50%" } }}>
                        <DialogContent className="AddNewUserDialogBox" style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <NewAdminViewController onDialogDismissed={this.dismissNewAdminDialog} onNewAdminCreated={this.onNewAdminCreated} />
                        </DialogContent>
                    </Dialog>


                    <Dialog onClose={this.dismissDeleteAdminDialog} aria-labelledby="customized-dialog-title" open={this.state.isDeleteAdminDialogOpen} fullWidth={false}
                        maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                        <DialogContent dividers style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <DeleteUserView onDialogDismissed={this.dismissDeleteAdminDialog} onUserRemoved={this.onUserDeleted} row= {this.state.deleteAdminRow}/>
                        </DialogContent>
                    </Dialog>
                </Grid >
                <CSSTransition
                    in={this.state.showSnackBar}
                    timeout={300}
                    classNames="alert"
                    unmountOnExit>
                    <Snackbar open={this.state.showSnackBar} autoHideDuration={6000} onClose={this.hideSnackBarError}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                        <AppAlert onClose={this.hideSnackBarError} severity={this.state.successAlert ? "success" :"error"} sx={{ width: "150%" }}>
                            {`${this.state.snackBarErrorMessage}`}
                        </AppAlert>
                    </Snackbar>
                </CSSTransition>
                <BackDropLoadingView handleBackDropClose={this.hideMainLoading} isLoading={this.state.mainLoadding} />
            </React.Fragment>
        } else {
            return <div className="AdminLoadingBox">
                <AppProgressBar color="primary" />
            </div>
        }
    }
}

export default UserLandingView;
